import { createApi } from '@reduxjs/toolkit/query/react';
import { sortAsc } from '@strata/helper-functions';

import { CLIENT_API_URL } from 'api/APIBase';
import { handleError, handleRTKQueryError } from 'services/Error';
import { getBaseQuery } from 'store/api/apiBaseQuery';

export const clientApi = createApi({
    baseQuery: getBaseQuery(CLIENT_API_URL),
    refetchOnMountOrArgChange: true,
    reducerPath: 'clientApi',
    endpoints: (build) => ({
        /** Gets list of benefit attributes for a benefit group */
        getBenefitAttributesByGroup: build.query({
            query: ({ payerId, benefitGroup }) =>
                `admin/${payerId}/benefits/${benefitGroup}/attributes`,
            transformResponse: (response) =>
                response
                    .map((attribute) => ({
                        attributeType: attribute.attribute_type,
                        attributeValue: JSON.parse(attribute.attribute_value ?? '[]').join(', '),
                    }))
                    .sort((a, b) => sortAsc(a.attributeType, b.attributeType)),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving benefit attributes.',
            ),
        }),
        getPrescriberFaxPref: build.query({
            query: ({ payerId, segmentId }) =>
                `admin/${payerId}/${segmentId}/payer_option/PRESC_FAX`,
            transformErrorResponse: handleRTKQueryError(
                'An error occurred while fetching prescriber fax payer option.',
            ),
        }),
        getReferralFaxStatus: build.query({
            query: ({ payerId, referralId }) =>
                `admin/${payerId}/referrals/${referralId}/fax_status`,
            transformErrorResponse: handleRTKQueryError(
                'An error occurred while fetching referral fax status.',
            ),
        }),
        getReferralMilestones: build.query({
            query: ({ payerId, referralId }) => ({
                url: `admin/${payerId}/referrals/${referralId}/milestones`,
                params: { include_internal: true },
            }),
            transformErrorResponse: (err) => {
                if (err.status !== 404) {
                    handleError('Error fetching referral milestones.', err);
                }
            },
        }),
        checkReferralRoute: build.query({
            query: ({
                payerId,
                segmentId,
                memberId,
                patientFirstName,
                patientDob,
                productServiceOid,
                isBrand,
                benefit,
                memberBenefitGroup,
                patientZip,
                supplierNpi,
                offerRate,
                prescriberNpi,
                marketplaceParticipants,
                placeOfService,
                therapeuticCategory,
                servicingSupplier,
                preferredPharmacyOffer,
            }) => ({
                url: `admin/${payerId}/referral_route_check`,
                method: 'POST',
                body: {
                    referral_id: null,
                    payer_id: payerId,
                    segment_id: segmentId,
                    member_id: memberId,
                    patient_first_name: patientFirstName,
                    patient_dob: patientDob,
                    product_service_oid: productServiceOid,
                    is_brand: isBrand,
                    benefit,
                    member_benefit_group: memberBenefitGroup,
                    patient_zip: patientZip,
                    supplier_npi: supplierNpi,
                    offer_rate: offerRate,
                    prescriber_npi: prescriberNpi,
                    prescriber_practice_npi: null,
                    marketplace_participants: marketplaceParticipants,
                    place_of_service: placeOfService,
                    therapeutic_category: therapeuticCategory,
                    check_only: true,
                    servicing_supplier: servicingSupplier,
                    preferred_pharmacy_offer: preferredPharmacyOffer
                        ? {
                              parent_pharmacy_npi: preferredPharmacyOffer.parentPharmacyNpi,
                              route_code: preferredPharmacyOffer.routeCode,
                              allow_route_override: preferredPharmacyOffer.allowRouteOverride,
                              // An offer rate with no rate means contract rate, so send none at all.
                              offer_rate: null,
                          }
                        : null,
                },
            }),
            transformResponse: (response) => ({
                routeCode: response?.scenario?.route_code ?? undefined,
                routeName: response?.scenario?.route_name ?? undefined,
                supplierNpi: response?.scenario?.supplier_npi ?? undefined,
                offerRate: response?.scenario?.offer_rate ?? undefined,
                referentPriceType: response?.scenario?.referent_price_type ?? undefined,
                useDeepestRate: response?.scenario?.use_deepest_rate ?? false,
            }),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred simulating the referral rate.',
            ),
        }),
        /** Sets a referral's marketplace participants to the suppliers that can service all 50 states */
        updateMarketplaceParticipantsToAllStateSuppliers: build.mutation({
            query: ({ payerId, referralId }) => ({
                url: `admin/${payerId}/referrals/${referralId}/marketplace_participants`,
                method: 'POST',
            }),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred updating marketplace participants.',
            ),
        }),
    }),
});

export const {
    useGetBenefitAttributesByGroupQuery,
    useGetPrescriberFaxPrefQuery,
    useGetReferralFaxStatusQuery,
    useGetReferralMilestonesQuery,
    useLazyCheckReferralRouteQuery,
    useUpdateMarketplaceParticipantsToAllStateSuppliersMutation,
} = clientApi;
